/*
Muestra la página de edición de clientes 
y envía los datos al servidor.
Autor: Carolina Rojas
Fecha: 25/11/2024
Modificaciones:
Le agregamos lo del ClientCre para tener solo una de modificacion y creacion
Agregamos un parametro adicional (onReturn) cuando estamos crendo y llamamos
desde otra pantalla, como ser, MessageProcess
*/

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useClientFetcher from '../hooks/useClientFetcher'
import useClientPoster from '../hooks/useClientPoster'
import { validateFields, Rule } from '../utils/validateForm'
import Client from '../interfaces/Client'
import useClientCrea from '../hooks/useClientCrea'
import { useAuth } from '../hooks/useAuth'
import { clientHrefReturn } from '../utils/utils'
import { validateAndIdentifyRut, RutType } from '../utils/rutValidation'

import { ButtonAction } from './Buttons'
import InputField from './InputField'

const clientIni: Client = {
  _id: '',
  organization: '',
  createdBy: '',
  email: '',
  firstname: '',
  lastname: '',
  nin: '',
  gender: '',
  address: '',
  phone: '',
  streetNumber: 0,
  createdAt: '',
  updatedAt: '',
  type: RutType.PERSON,
  __v: 1,
}

const ClientEdit: React.FC<{ idOverride?: boolean; onReturn?: (id: string) => void }> = ({ idOverride, onReturn }) => {
  const { fetchClientDocs } = useClientFetcher()
  const { errorPostClient, postClient } = useClientPoster()
  const [title, setTitle] = useState<string>('')
  const [btnLabel, setBtnLabel] = useState<string>('')
  const [formData, setFormData] = useState<Client | null>(clientIni)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [success] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [creandoSw, setCreandoSw] = useState<boolean>(false)
  const { errorCreaClient, loadingPost, creaClient } = useClientCrea()
  /*
  Cerrojo contra el doble envio. `loadingPost` sirve para pintar el boton, pero no
  alcanza para frenar el segundo click: setState es asincrono, asi que entre el primer
  click y el re-render hay una ventana en la que el handler vuelve a entrar. El 31/08
  dos POST salieron con 5ms de diferencia y crearon el cliente Enrique Teodoro dos
  veces. Un ref se actualiza en el acto y cierra esa ventana.
  */
  const submitting = useRef(false)
  const { userData, vehicleForDoc, selectedPair, setSelectedPair } = useAuth()
  const { id: routeId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const id = idOverride ? '0' : routeId
  useEffect(() => {
    if (!id) {
      setLocalError('ID is not defined.')
      setFormData(null)

      return // Exit early if `id` is undefined y estamos en "edit"
    }

    // Para llamarlo sin parametros

    if (id === '0') {
      setCreandoSw(true)
      setTitle('Crear cliente')
      setBtnLabel('Crear')

      return
    }
    setTitle('Modificar cliente')
    setBtnLabel('Modificar')
    const fetchDataAsync = async () => {
      const clientFull = await fetchClientDocs(id)

      if (clientFull) {
        setFormData(clientFull)
      }
    }
    fetchDataAsync()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (formData) {
      setFormData(() => {
        return {
          ...formData,
          [name]: value,
        }
      })
    }
  }

  const handleEditClick = async () => {
    if (!formData) return

    if (submitting.current) return

    console.log('Form data before validation:', formData)

    // Validar los campos
    const isCompany = formData?.type === RutType.COMPANY;
    
    const validationFields = [
      { name: 'firstname', value: formData?.firstname, rule: Rule.REQUIRED },
      { name: 'email', value: formData?.email, rule: Rule.REQUIRED },
    ];
    
    if (!isCompany) {
      validationFields.push({ name: 'lastname', value: formData?.lastname, rule: Rule.REQUIRED });
    }

    const validationErrors = validateFields(validationFields)

    if (formData?.nin) {
      const rutVal = validateAndIdentifyRut(formData.nin);
      const expectedType = isCompany ? RutType.COMPANY : RutType.PERSON;
      if (!rutVal.isValid) {
        validationErrors.nin = 'RUT inválido';
      } else if (rutVal.type !== expectedType) {
        validationErrors.nin = isCompany ? 'Debe ser RUT de Empresa' : 'Debe ser RUT de Persona Natural';
      }
    }

    console.log('Validation errors:', validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any

      return
    }

    let response

    submitting.current = true

    try {
      if (creandoSw) {
        response = await creaClient({
          ...formData,
          organization: userData?.organization?._id ?? '',
          createdBy: userData?.profile?._id ?? '',
        })
      } else {
        response = await postClient(formData)
      }
    } finally {
      /*
      Se libera SIEMPRE, incluso si la peticion falla: si no, un error de red dejaria el
      boton trabado para siempre y la persona no podria reintentar.
      */
      submitting.current = false
    }

    const myId = creandoSw ? response : id

    if (!response) {
      return
    }

    if (creandoSw && onReturn) {
      // Aca cuando estamos creando y siendo llamados por
      // el "Deal"
      onReturn(myId!)

      return
    }

    // Si estamos creando para crear un documento, entonces tenemos
    // que ir de vuelta a la VehCliSelection con este cliente
    const goTo: string = clientHrefReturn(vehicleForDoc, myId!)
    // Si estamos creando, ademas tenemos que setear el vehiculo creado
    // en la variable global selectedPair
    const vehicleIdAux = vehicleForDoc > 0 ? myId! : ''

    setSelectedPair({ ...selectedPair, clientId: vehicleIdAux })
    navigate(goTo)
  }

  return (
    <div className="max-w-3xl p-6 bg-white shadow-md rounded-lg space-y-4">
      <h2 className="flex justify-center text-2xl font-semibold text-gray-800 mb-4">{title}</h2>
      {errorPostClient && <div className="error-message text-red-500">{errorPostClient}</div>}

      <div className="grid gap-y-6">
        <div className="mb-2">
          <label className="block text-gray-700 font-medium mb-2">Tipo de Cliente</label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value={RutType.PERSON}
                checked={formData?.type === RutType.PERSON || !formData?.type}
                onChange={handleInputChange}
                className="mr-2"
              />
              Persona Natural
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value={RutType.COMPANY}
                checked={formData?.type === RutType.COMPANY}
                onChange={handleInputChange}
                className="mr-2"
              />
              Persona Jurídica (Empresa)
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData?.type === RutType.COMPANY ? (
            <InputField
              label="Razón Social"
              name="firstname"
              value={formData?.firstname ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
          ) : (
            <>
              <InputField
                label="Nombre"
                name="firstname"
                value={formData?.firstname ?? ''}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Apellido"
                name="lastname"
                value={formData?.lastname ?? ''}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </>
          )}
          <div>
            <InputField
              label="RUT"
              name="nin"
              value={formData?.nin ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
            {formData?.nin && (() => {
              const rutVal = validateAndIdentifyRut(formData.nin);
              const isNatural = formData?.type !== RutType.COMPANY;
              const expectedType = isNatural ? RutType.PERSON : RutType.COMPANY;
              
              let msg = '';
              let isError = false;

              if (!rutVal.isValid) {
                msg = 'RUT Inválido';
                isError = true;
              } else if (rutVal.type !== expectedType) {
                msg = isNatural 
                  ? 'RUT corresponde a Empresa, pero seleccionó Persona Natural.' 
                  : 'RUT corresponde a Persona Natural, pero seleccionó Persona Jurídica.';
                isError = true;
              } else {
                msg = `Válido - ${isNatural ? 'Persona Natural' : 'Persona Jurídica (Empresa)'}`;
              }

              return (
                <div className={`mt-1 text-sm font-medium ${isError ? 'text-red-500' : 'text-green-600'}`}>
                  {msg}
                </div>
              );
            })()}
          </div>
          {formData?.type !== RutType.COMPANY && (
            <>
              <InputField
                label="Fecha de Nacimiento"
                name="dateBirth"
                value={formData?.dateBirth ?? ''}
                type="date"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <div>
                <label htmlFor="gender" className="block text-gray-700 font-medium mb-2">
                  Género
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData?.gender ?? ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="" disabled>
                    Selecciona el género
                  </option>
                  <option value="GENDER.MALE">Masculino</option>
                  <option value="GENDER.FEMALE">Femenino</option>
                </select>
              </div>
            </>
          )}
        </div>
        <div className="grid gap-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Email"
              name="email"
              value={formData?.email ?? ''}
              type="email"
              handleInputChange={handleInputChange}
              errors={errors}
            />
            <InputField
              label="Teléfono"
              name="phone"
              value={formData?.phone ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
          </div>
          <div>
            <InputField
              label="Dirección"
              name="address"
              value={formData?.address ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
            <div>
              Importante: Ingresa la calle, comuna y ciudad separados por coma. Por ejemplo: Los Leones, Providencia,
              Santiago
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Número"
              name="streetNumber"
              value={formData?.streetNumber ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
            <InputField
              label="Casa, departamento u oficina"
              name="addressComponents"
              value={formData?.addressComponents ?? ''}
              type="text"
              handleInputChange={handleInputChange}
              errors={errors}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <ButtonAction label={btnLabel} handleButtonClick={handleEditClick} disabled={loadingPost} />
      </div>
      {success && <div className="success-message">{success}</div>}
      <div> {errorPostClient} </div>
      <div> {errorCreaClient} </div>
      <div>{localError}</div>
    </div>
  )
}

export default ClientEdit
